import type { Auth0Client, IdToken } from '@auth0/auth0-spa-js';
import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import { createContext, useContext, useState, useEffect } from 'react';
import { identify } from '@peloton/analytics/analytics';
import authClient, { AuthEnv } from '@peloton/auth/authClient';
import { getAuth0Client, loginErrors } from '@peloton/auth/OauthProvider';
import { SupportedTLD, toMatchingTld } from '@peloton/internationalize/models/locale';
import { logoutPost } from '@engage/api-v2/generated';
import { getLocalizedUrl } from '@engage/api-v2/generated/services/LocalesService';
import {
  claimRemoteDeviceActivation,
  claimRemoteAddSharedUser,
  getRemoteAddSharedUser,
  cancelRemoteAddSharedUser,
  updateRemoteAddSharedUserUserOnboardingEnded,
} from '@engage/api-v2/generated/services/UsersService';
import type { AuthAction } from '@engage/auth/oauthFragment';
import { LoginPage } from '@engage/auth/oauthFragment';
import { getCookieValue } from '@engage/cookies/getCookieValue';
import { AnalyticsEvent } from '../../analytics';
import { useLocale } from '../../copy/context';
import { toEnvVariables } from '../../env/env';
import { useTrackingCallback } from '../../hooks';
import { useIsSsoOnRemoteActivationEnabled } from '../../hooks/useIsSSOOnRemoteActivationEnabled';
import pollingWithType from '../../utils/pollingWithType';
import { useView } from '../ViewProvider';
import { persistAuthData, getPersistedAuthData, clearAuthData } from './persistAuthData';
import { engageApi } from './singletons';

const SWITCH_USERS_PARAM = 'switchUsers';
const AUTH_PROD_DOMAIN = 'auth.onepeloton.com';
const AUTH_STAGE_DOMAIN = 'auth-stage.onepeloton.com';
const IDENTITY_PROD_DOMAIN = 'identity.onepeloton.com';
const IDENTITY_STAGE_DOMAIN = 'identity-stage.onepeloton.com';
const userIdKey = 'http://onepeloton.com/user_id'; // this comes from auth0 IdToken
const POLLING_INTERVAL = 5000;

type Context = {
  authEnv: AuthEnv;
  activateDevice: () => Promise<void>;
  verifyDevice: (userCode: string, opts?: { errorCode?: string }) => Promise<void>;
  changeUsers: () => Promise<void>;
  getUserCodeDeviceSerial: (userCode: string) => Promise<string>;
  user: IdToken | null;
  remoteActivationId: string;
  sessionTLD: SupportedTLD;
  logoutLegacySession: () => void;
};

const AuthContext = createContext<Context>({
  authEnv: AuthEnv.Stage,
  activateDevice: () => new Promise(() => null),
  verifyDevice: () => new Promise(() => null),
  changeUsers: () => new Promise(() => null),
  getUserCodeDeviceSerial: () => new Promise(() => null),
  user: null,
  remoteActivationId: '',
  sessionTLD: SupportedTLD.Com,
  logoutLegacySession: () => {},
});

export const useAuth = () => useContext(AuthContext);

const createOauthClient = ({
  audience,
  clientId,
  scope,
  domain,
  redirectUri,
  caller,
}: {
  audience: string;
  clientId: string;
  scope: string;
  domain: string;
  redirectUri: string;
  caller: string;
}) => {
  if (!clientId) {
    datadogRum.addAction('missing client_id', {
      caller: caller,
    });
  }

  return getAuth0Client({
    clientId,
    domain,
    authorizationParams: {
      audience,
      scope,
      redirect_uri: redirectUri,
    },
    useRefreshTokens: false,
    cacheLocation: 'memory',
  });
};

let userCodeStateInterval: NodeJS.Timer | null;

const isProd = toEnvVariables().ENVIRONMENT === 'PRODUCTION';
export const domain = isProd ? AUTH_PROD_DOMAIN : AUTH_STAGE_DOMAIN;
export const identityDomain = isProd ? IDENTITY_PROD_DOMAIN : IDENTITY_STAGE_DOMAIN;
const authEnv = isProd ? AuthEnv.Prod : AuthEnv.Stage;

const engageApiClient = engageApi(authEnv);

const reportRemoteActivationError = (
  step: string,
  error: unknown,
  context?: Record<string, string>,
) => {
  const activationError =
    error instanceof Error
      ? error
      : new Error(`Remote activation error [${step}]: ${String(error)}`);
  activationError.name = 'RemoteActivationError';
  datadogRum.addError(activationError, {
    source: 'remote-activation',
    step,
    ...context,
  });
};

const OauthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const locale = useLocale();
  const [redirectUri, setRedirectUri] = useState('');
  const [oauthClient, setOauthClient] = useState<Auth0Client | null>(null);
  const client = authClient(authEnv, SupportedTLD.Com, { 'Peloton-Platform': 'web' });
  const router = useRouter();
  const [user, setUser] = useState<IdToken | null>(null);

  const [remoteActivationId, setRemoteActivationId] = useState('');
  const [sessionTLD, setSessionTLD] = useState(SupportedTLD.Com);

  const { setView } = useView();
  const { track } = useTrackingCallback();
  const SsoDecision = useIsSsoOnRemoteActivationEnabled();

  const cancelDeviceFlow = async (uCode?: string) => {
    const { userCode } = getPersistedAuthData(authEnv);

    try {
      setView('Loading');
      await client.post('/oauth/device/cancel', { user_code: uCode ?? userCode });
      clearAuthData(authEnv);
      setView('ActivationCancelled');
    } catch (e) {
      reportRemoteActivationError('cancel-device-flow', e);
      setView('ActivationCancelled');
    }
  };

  const getAndSetUser = async (
    newAuthClient: Auth0Client,
    uCode?: string,
    action?: AuthAction,
  ) => {
    try {
      const tokenClaims = await newAuthClient.getIdTokenClaims();

      if (!tokenClaims) {
        throw new Error('No user token claims');
      }

      setUser(tokenClaims);
      trackMemberLoggedIn(uCode, action);
      return tokenClaims;
    } catch (e) {
      reportRemoteActivationError('get-user-token-claims', e);
      cancelDeviceFlow(uCode);
    }
    return null;
  };

  const checkForLoginErrors = async (redirect_uri: string) => {
    if (!!window) {
      const queryParams = new URLSearchParams(window.location.search);
      const errorMessage = queryParams.get('error_description');
      const match = loginErrors.exec(errorMessage ?? '');
      if (!!match) {
        const [code] = match;
        setView('Loading');
        // we've run into an error and must perform a global logout
        const tld = SupportedTLD.Com;
        const returnTo = `${authEnv}${tld}/sso/global_logout?continue=${encodeURIComponent(
          `${redirect_uri}`,
        )}?errorMessage=${code}`;

        const {
          audience,
          clientId,
          scope,
          userCode: uCode,
        } = getPersistedAuthData(authEnv);

        try {
          await client.post('/oauth/device/cancel', { user_code: uCode });
        } catch (e) {
          reportRemoteActivationError('cancel-user-code-on-login-error', e);
        }

        const newAuthClient = createOauthClient({
          audience,
          clientId,
          scope,
          redirectUri: redirect_uri,
          domain,
          caller: 'checkForLoginErrors',
        });

        newAuthClient?.logout({
          logoutParams: {
            returnTo,
          },
        });
      }
    }
  };

  useEffect(() => {
    const redirect_uri = `${window.location.origin}${window.location.pathname}`;
    setRedirectUri(redirect_uri);

    checkForLoginErrors(redirect_uri);

    // on return from universal logout, the auth client needs to be reinstantiated
    if (
      !!window &&
      (window?.location.search.includes(`?${SWITCH_USERS_PARAM}`) ||
        window?.location.search.includes(`&${SWITCH_USERS_PARAM}`))
    ) {
      setView('Loading');
      const {
        audience,
        clientId,
        scope,
        action: newAction,
      } = getPersistedAuthData(authEnv);
      const newAuthClient = createOauthClient({
        audience,
        clientId,
        scope,
        redirectUri: redirect_uri,
        domain,
        caller: 'switchUsers',
      });

      newAuthClient?.loginWithRedirect({
        authorizationParams: {
          redirect_uri,
          prompt: 'login',
        },
        fragment: JSON.stringify({
          type: newAction === 'login' ? LoginPage.Standard : LoginPage.Activation,
          action: newAction,
          message: '',
          locale,
        }),
        appState: { shouldActivateDevice: true },
      });
    } else if (
      !!window &&
      (window?.location.search.includes('?code') ||
        window?.location.search.includes('&code'))
    ) {
      // on return from universal login page, the auth client needs to be reinstantiated
      setView('Loading');
      const {
        audience,
        clientId,
        scope,
        userCode: uCode,
        action,
      } = getPersistedAuthData(authEnv);

      pollUserCodeState(uCode);
      const newAuthClient = createOauthClient({
        audience,
        clientId,
        scope,
        redirectUri: redirect_uri,
        domain,
        caller: 'code',
      });

      setOauthClient(newAuthClient);

      (async () => {
        try {
          await newAuthClient.handleRedirectCallback();

          router.replace(window.location.pathname, undefined, { shallow: true });

          const deviceAction = action || undefined;
          getAndSetUser(newAuthClient, uCode, deviceAction);

          setView('UserSelect');
        } catch (e) {
          reportRemoteActivationError('handle-redirect-callback', e);
          cancelDeviceFlow(uCode);
        }
      })();
    } else {
      setView('CodeInput');
    }
  }, []);

  const trackMemberLoggedIn = (activationCode?: string, action?: AuthAction) => {
    const loginMethod = getCookieValue('LOGIN_METHOD');
    if (action === 'device_activation') {
      track(AnalyticsEvent.MemberIsLoggedIn, {
        activationCode,
        loginMethod: loginMethod,
        variation: SsoDecision.variationKey,
      });
    } else {
      track(AnalyticsEvent.MemberIsLoggedIn, {
        activationCode,
      });
    }
  };

  const changeUsers = async () => {
    // first perform a global logout which will redirect the user (this will log the user out of account app as well)
    // upon return, we call loginWithRedirect
    const tld = SupportedTLD.Com;
    const returnTo = `${authEnv}${tld}/sso/global_logout?continue=${encodeURIComponent(
      `${redirectUri}?${SWITCH_USERS_PARAM}=true`,
    )}`;

    await oauthClient?.logout({ logoutParams: { returnTo } });
  };

  const checkUserCodeState = async (uCode: string) => {
    const response = await client.post('/oauth/device/user_code/state', {
      user_code: uCode,
    });
    return response;
  };

  const getUserCodeDeviceSerial = async (uCode: string): Promise<string> => {
    try {
      const response = await checkUserCodeState(uCode);
      const tabletSerial = response.headers['tablet-serial'];
      return tabletSerial || '';
    } catch (e) {
      reportRemoteActivationError('get-device-serial', e);
      return '';
    }
  };

  const pollUserCodeState = (uCode: string) => {
    userCodeStateInterval = setInterval(async () => {
      try {
        const response = await checkUserCodeState(uCode);
        if (response.data.user_code_state === 'cancelled') {
          setView('ActivationCancelled');
          if (userCodeStateInterval) {
            clearInterval(userCodeStateInterval as unknown as number);
          }
          clearAuthData(authEnv);
        }
      } catch (e) {
        reportRemoteActivationError('poll-user-code-state', e);
        setView('ActivationCancelled');
        if (userCodeStateInterval) {
          clearInterval(userCodeStateInterval as unknown as number);
        }
        clearAuthData(authEnv);
      }
    }, POLLING_INTERVAL);
  };

  const verifyDevice = async (
    uCode: string,
    opts?: { errorCode?: string },
  ): Promise<void> => {
    const { data } = await client.post('/oauth/device/verify', {
      user_code: uCode,
    });

    const fallbackRedirect = `${window.location.origin}${window.location.pathname}`;

    pollUserCodeState(uCode);
    persistAuthData(authEnv, { ...data, userCode: uCode });

    track(AnalyticsEvent.EnteredActivationCode, {
      activationCode: uCode,
      actionType: data.action,
    });

    const newAuthClient = createOauthClient({
      audience: data.audience,
      clientId: data.clientId,
      scope: data.scope,
      redirectUri: SsoDecision.enabled ? fallbackRedirect : redirectUri,
      domain,
      caller: 'verifyDevice',
    });

    setOauthClient(newAuthClient);

    // this will redirect to auth0 to check if member is logged in
    // if the member is logged in, they will automatically get redirected back to the activation app without intervention
    // if they are not logged in, it will show them the login page
    await newAuthClient.loginWithRedirect({
      authorizationParams: {
        redirect_uri: SsoDecision.enabled ? fallbackRedirect : redirectUri,
      },
      fragment: JSON.stringify({
        type: data.action === 'login' ? LoginPage.Standard : LoginPage.Activation,
        prompt: 'none',
        action: data.action,
        message: opts?.errorCode ?? '',
        locale,
      }),
      appState: { shouldActivateDevice: false },
    });
  };

  const activateDevice = async (maybeAuthClient?: Auth0Client) => {
    const { userCode, action } = getPersistedAuthData(authEnv);

    try {
      setView('Loading');
      const newAuthClient = maybeAuthClient || oauthClient;
      const token = await newAuthClient?.getTokenSilently({ cacheMode: 'off' });
      if (!token) {
        throw new Error('No user token');
      }

      const userId = user?.[userIdKey] ?? '';
      const newUser = !user?.preferred_username;

      try {
        if (userId) {
          identify({ userId });
        }
      } catch (e) {
        reportRemoteActivationError('segment-identify', e);
      }

      await client.post(
        '/oauth/device/activate',
        { user_code: userCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (action === 'device_activation') {
        try {
          const localizedResponse = await getLocalizedUrl(
            engageApiClient,
            {
              url: 'https://members.onepeloton.com',
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          setSessionTLD(toMatchingTld(localizedResponse.data.url));
        } catch (e) {
          reportRemoteActivationError('get-localized-url', e);
        }

        const response = await pollingWithType(
          () =>
            claimRemoteDeviceActivation(engageApiClient, userId, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }),
          res => res.data.isClaimed ?? false,
          err => {
            if (err.response?.status && err.response?.status === 404) {
              return true;
            } else {
              if (userCodeStateInterval) {
                clearInterval(userCodeStateInterval as unknown as number);
              }
              // we don't have a remoteDeviceActivationId so we aren't calling cancel here
              clearAuthData(authEnv);
              setView('ActivationCancelled');
              return false;
            }
          },
        );

        setRemoteActivationId(response?.data.id ?? '');

        track(AnalyticsEvent.ActivatedDeviceFlow, {
          activationCode: userCode,
          actionType: action,
          platformType: response?.data.platform,
          productModel: response?.data.productModel,
          deviceId: response?.data.deviceId,
          userId,
        });

        setView('PageRedirect');
      } else if (action === 'add_account') {
        const response = await pollingWithType(
          () =>
            claimRemoteAddSharedUser(engageApiClient, userId, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }),
          res => res.data.isClaimed ?? false,
          err => {
            if (err.response?.status && err.response?.status === 404) {
              return true;
            } else {
              // we don't have a remoteAddSharedUserId so we aren't calling cancel here
              clearAuthData(authEnv);
              setView('ActivationCancelled');
              return false;
            }
          },
        );
        const remoteAddSharedUserId = response?.data.id ?? '';
        setRemoteActivationId(remoteAddSharedUserId);
        // Once we claim, we are waiting on Tiger to also claim said
        // user on the device, and we're done.
        await pollingWithType(
          () =>
            getRemoteAddSharedUser(engageApiClient, userId, remoteAddSharedUserId, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }),
          res => res.data.hasSharedUserBeenAdded ?? false,
          err => {
            cancelRemoteAddSharedUser(
              engageApiClient,
              userId,
              remoteAddSharedUserId,
              undefined,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            clearAuthData(authEnv);
            setView('ActivationCancelled');
            return false;
          },
        );
        if (!newUser) {
          await updateRemoteAddSharedUserUserOnboardingEnded(
            engageApiClient,
            userId,
            remoteAddSharedUserId,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
        }
        if (userCodeStateInterval) {
          clearInterval(userCodeStateInterval as unknown as number);
        }

        track(AnalyticsEvent.ActivatedDeviceFlow, {
          activationCode: userCode,
          actionType: action,
        });

        setView(newUser ? 'PageRedirect' : 'ActivationComplete');
      } else {
        track(AnalyticsEvent.ActivatedDeviceFlow, {
          activationCode: userCode,
          actionType: action,
        });

        clearAuthData(authEnv);
        if (userCodeStateInterval) {
          clearInterval(userCodeStateInterval as unknown as number);
        }
        setView('ActivationComplete');
      }
      newAuthClient?.logout({ openUrl: false });
    } catch (e) {
      reportRemoteActivationError('activate-device', e, { action: action ?? 'unknown' });
      cancelDeviceFlow(userCode);
    }
  };

  // Some applications will prefer legacy session auth, overriding the Oauth login that occurs as part of device activation, causing issues.
  const logoutLegacySession = () => {
    logoutPost(engageApiClient, 'web');
  };

  return (
    <AuthContext.Provider
      value={{
        authEnv,
        activateDevice,
        verifyDevice,
        changeUsers,
        getUserCodeDeviceSerial,
        user,
        remoteActivationId,
        sessionTLD,
        logoutLegacySession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default OauthProvider;
